import { Controller } from "@hotwired/stimulus"

// RL-113 + RL-114: coordinates the coach shell's tab UI from ONE source of
// truth — the ?tab= query param — with no reload.
//
// Two surfaces, one param:
//  - Desktop right panel (RL-113): Plan/Docs/Roofers segmented tabs + panes.
//    "chat" has no panel pane, so it maps to the "plan" pane here.
//  - Mobile bottom nav (RL-114): Chat/Plan/Docs/Roofers. On mobile ?tab=chat
//    shows the chat column and hides the panel; a non-chat tab hides the chat
//    column and shows the panel full-width. The chat column is never removed
//    from the DOM (draft text + scroll survive switches — frontend contract #2
//    is upheld server-side; this only toggles classes).
//
// Frontend contracts honored:
//  #5 active state derived in connect() from ONE source of truth (location.search),
//     never snapshotted from server-rendered classes;
//  #7 the popstate listener is removed in disconnect().
export default class extends Controller {
  static targets = ["tab", "panel", "aside", "chatColumn", "bottomTab", "bottomNav"]
  static values = { mobileDefault: { type: String, default: "chat" } }

  connect() {
    this.onPopState = () => this.render()
    window.addEventListener("popstate", this.onPopState)
    this.render()
  }

  disconnect() {
    window.removeEventListener("popstate", this.onPopState)
  }

  // Click on any tab (panel segmented or mobile bottom nav): switch in place
  // and sync the URL, no reload.
  select(event) {
    event.preventDefault()
    const slug = event.currentTarget.dataset.tab
    if (!slug) return

    const url = new URL(window.location.href)
    url.searchParams.set("tab", slug)
    window.history.replaceState({}, "", url)
    this.render()
  }

  // Raw ?tab= value across the full mobile vocabulary (default = mobileDefault).
  mobileTab() {
    const known = ["chat", "plan", "docs", "roofers"]
    const requested = new URLSearchParams(window.location.search).get("tab")
    return known.includes(requested) ? requested : this.mobileDefaultValue
  }

  // The desktop panel has no chat pane — chat maps to plan.
  panelTab() {
    const tab = this.mobileTab()
    return tab === "chat" ? "plan" : tab
  }

  render() {
    const mobile = this.mobileTab()
    const panelActive = this.panelTab()
    const showChat = mobile === "chat"

    // Desktop panel panes.
    this.panelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.tab !== panelActive
    })

    // Desktop panel segmented-tab active state.
    this.tabTargets.forEach((tab) => {
      const selected = tab.dataset.tab === panelActive
      tab.setAttribute("aria-selected", selected ? "true" : "false")
      tab.classList.toggle("bg-surface-card", selected)
      tab.classList.toggle("text-ink", selected)
      tab.classList.toggle("shadow-card", selected)
      tab.classList.toggle("text-muted", !selected)
    })

    // Mobile column visibility. `hidden` here is the Tailwind display utility
    // (mobile-only); the panel: breakpoint variant keeps both columns visible
    // on desktop regardless of these toggles, so we only touch the base class.
    if (this.hasChatColumnTarget) {
      this.chatColumnTarget.classList.toggle("hidden", !showChat)
      this.chatColumnTarget.classList.toggle("flex", showChat)
    }
    if (this.hasAsideTarget) {
      this.asideTarget.classList.toggle("hidden", showChat)
      this.asideTarget.classList.toggle("flex", !showChat)
    }

    // Mobile bottom-nav active state.
    this.bottomTabTargets.forEach((tab) => {
      const current = tab.dataset.tab === mobile
      if (current) {
        tab.setAttribute("aria-current", "page")
        tab.classList.add("text-clay-700")
        tab.classList.remove("text-muted")
      } else {
        tab.removeAttribute("aria-current")
        tab.classList.remove("text-clay-700")
        tab.classList.add("text-muted")
      }
    })

    // rAF rescroll: when the chat column becomes visible, its messages pane had
    // scrollHeight 0 while hidden, so pin to bottom on the next frame. The chat
    // controller owns the actual scroll; we just ask it to re-pin.
    if (showChat) {
      requestAnimationFrame(() => {
        this.element.dispatchEvent(
          new CustomEvent("panel-tabs:chat-shown", { bubbles: true })
        )
      })
    }
  }
}
